/**
 * i18n Module
 *
 * Usage:
 *   import { useTranslation } from '../i18n';
 *
 *   function MyComponent() {
 *     const { t, locale } = useTranslation();
 *     return <h1>{t('common.loading')}</h1>;</h1>
 *   }
 *
 * EN namespace lazy-loading:
 *   Non-core EN namespaces (dashboard, application flow, blog, etc.) are
 *   loaded lazily alongside their page chunks in App.tsx via loadEnNamespaces.
 *   useTranslation subscribes to namespace load events and triggers a re-render
 *   whenever a new group merges into messages.en, so t() always returns fresh
 *   strings without requiring any change to individual page components.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';
import messages, { loadLocale, subscribeToEN } from './messages';
import type { Messages } from './messages';

export type { Language };
export { useLanguage };
export { messages };

// Re-export so App.tsx (and anywhere else) can import from one place
export { loadEnNamespaces } from './messages';
export type { EnGroup } from './messages/en';

/**
 * Interpolation params for translation strings
 */
export type TranslationParams = Record<string, string | number>;

/**
 * Get a nested value from an object by dot-separated path
 */
function getNestedValue(obj: unknown, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }

  return typeof current === 'string' ? current : undefined;
}

/**
 * Interpolate variables in a translation string
 * Replaces {varName} with the corresponding value from params
 */
function interpolate(text: string, params?: TranslationParams): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    const value = params[key];
    return value !== undefined ? String(value) : match;
  });
}

/**
 * Translation hook
 *
 * EN core is synchronous (bundled). Non-core EN namespaces arrive lazily as
 * page chunks load — this hook subscribes to those load events and re-renders
 * so that t() always reflects the latest available messages.
 *
 * ES and FR are loaded lazily on first language switch. While loading, the
 * hook falls back to EN so there is no blank-text flash.
 */
export function useTranslation() {
  const { language, setLanguage, languageConfig } = useLanguage();

  // Track whether the requested locale bundle is ready.
  // EN is always ready (core is synchronous); others need a dynamic import.
  const [localeReady, setLocaleReady] = useState<Language>(
    messages[language] ? language : 'en'
  );

  // Monotonically-increasing counter that bumps whenever a new EN namespace
  // group is merged into messages.en. Used as a useMemo dependency so
  // currentMessages refreshes after each group load.
  const [enVersion, setEnVersion] = useState(0);

  // Subscribe to EN namespace load events for the lifetime of this hook.
  useEffect(() => {
    return subscribeToEN(() => setEnVersion(v => v + 1));
  }, []);

  // Load ES / FR locale bundles on language switch.
  useEffect(() => {
    if (language === 'en' || messages[language]) {
      setLocaleReady(language);
      return;
    }
    // Kick off the async chunk download for ES or FR
    loadLocale(language as 'es' | 'fr').then(() => {
      setLocaleReady(language);
    });
  }, [language]);

  // Re-derive currentMessages when locale changes OR when EN grows.
  // enVersion is only meaningful for EN; it's a no-op for ES/FR (their
  // bundles are complete when loaded).
  const currentMessages = useMemo((): Messages => {
    return messages[localeReady] || messages.en;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeReady, enVersion]);

  const t = useCallback((key: string, params?: TranslationParams): string => {
    let text = getNestedValue(currentMessages, key);

    if (text === undefined && language !== 'en') {
      text = getNestedValue(messages.en, key);
    }

    if (text === undefined) {
      console.warn(`Translation missing: ${key} (${language})`);
      return key;
    }

    return interpolate(text, params);
  }, [currentMessages, language]);

  const hasTranslation = useCallback((key: string): boolean => {
    return getNestedValue(currentMessages, key) !== undefined ||
           getNestedValue(messages.en, key) !== undefined;
  }, [currentMessages]);

  return {
    t,
    locale: language,
    setLocale: setLanguage,
    languageConfig,
    hasTranslation,
    messages: currentMessages,
  };
}

/**
 * Standalone translation function for use outside React components.
 * Only works synchronously for EN (core always loaded, groups if previously
 * fetched) or a previously loaded ES/FR locale.
 */
export function translate(
  key: string,
  params?: TranslationParams,
  locale: Language = 'en'
): string {
  const localeMessages = messages[locale] || messages.en;
  let text = getNestedValue(localeMessages, key);

  if (text === undefined && locale !== 'en') {
    text = getNestedValue(messages.en, key);
  }

  if (text === undefined) return key;

  return interpolate(text, params);
}

/**
 * T Component - for JSX translations with interpolation
 */
interface TProps {
  id: string;
  values?: TranslationParams;
  fallback?: string;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
}

export function T({ id, values, fallback, as: Component = 'span', className }: TProps): React.ReactElement {
  const { t, hasTranslation } = useTranslation();
  const text = hasTranslation(id) ? t(id, values) : (fallback || id);
  return React.createElement(Component, { className }, text);
}

// Re-export dynamic translation utilities
export {
  useDynamicTranslation,
  useDynamicTranslations,
  usePrefetchTranslations,
} from './useDynamicTranslation';
export type {
  DynamicTranslationResult,
  DynamicTranslationsResult,
  DynamicTranslationOptions,
} from './useDynamicTranslation';

// Re-export cache utilities
export { translationCache } from './cache';

export default useTranslation;